import type { ErrorCode, ValidationIssue } from './api';
import { currentMessageLanguage, validationFailedMessage } from './values';

/**
 * Error with an API error code. Thrown by services; the API maps it to
 * `{ error: { code, message, details } }` with the matching HTTP status.
 */
export class AppError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export const notFound = (what: string) => new AppError('not_found', `${what} not found`);
export const forbidden = (message = 'You do not have permission to do this') =>
  new AppError('forbidden', message);
export const conflict = (message: string, details?: unknown) => new AppError('conflict', message, details);
/** `validation_failed` with per-path issues; the default message is in the request's language. */
export const validationFailed = (
  issues: ValidationIssue[],
  message = validationFailedMessage(currentMessageLanguage()),
) => new AppError('validation_failed', message, { issues });
export const badRequest = (message: string, details?: unknown) =>
  new AppError('bad_request', message, details);

export function isAppError(err: unknown): err is AppError {
  return (
    err instanceof AppError ||
    (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AppError')
  );
}
